import { defineStore } from 'pinia';
import { computed, reactive, ref, toRefs, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useVersion } from '@/stores/version';
import { type ApiCase, getCase } from '@/api/case';
import { client } from '@/api/http';
import { AxiosError, type AxiosResponse, HttpStatusCode } from 'axios';
import { getIdFromUrl } from '@/api/getIdFromUrl';
import { type ApiStudy, getStudy } from '@/api/study/study';
import type { Template } from '@/domain/template/template';
import { createTemplate } from '@/api/template/createTemplate';
import { asyncTimeout } from '@/util';
import { getTemplate } from '@/api/template/getTemplate';
import { caseIdFromRoute } from '@/router';

export type StudyProcessOptionsRepresentation = {
    /**
     * Whether workflow should create 'notifications' for the user as to progress. This allows
     the study workflow code to be used for both interactive and non-interactive workflows (i.e.
     a study processing where we don't want to alert the user).
     */
    create_notifications: boolean;

    /**
     * Whether workflow should send a success or failure email to the surgeon/case creator. This allows
     * the study workflow code to be used for both interactive and non-interactive workflows (i.e.
     * a study processing where we don't want to alert the user).
     */
    send_email: boolean;
};

export type DuplicateStudyOperation = 'files' | 'models'

type _AdminCaseState = {
    caseId: number | null;
    case: ApiCase | null;
    studies: ApiStudy[];
    templates: Template[];
}

export const useCaseAdminStore = defineStore('admin', () => {
    const _route = useRoute();

    const _caseState = reactive<_AdminCaseState>({
        caseId: null,
        case: null,
        studies: [],
        templates: [],
    });

    const shouldPoll = ref<boolean>(false);

    let abortPolling: (() => Promise<void>) | null = null;

    watch(
        shouldPoll,
        async (shouldPoll: boolean) => {
            if (abortPolling) {
                await abortPolling();
            }
            _caseState.caseId = null;
            _caseState.case = null;
            _caseState.studies = [];
            _caseState.templates = [];

            if (!shouldPoll || _route.params.id === undefined) {
                return;
            }

            const caseId = caseIdFromRoute(_route);
            const aborter = new AbortController();

            const polling = _pollCase(
                caseId,
                (state_) => {
                    _caseState.caseId = state_.caseId;
                    _caseState.case = state_.case;
                    _caseState.studies = state_.studies;
                    _caseState.templates = state_.templates;
                },
                aborter.signal,
            )

            abortPolling = async () => {
                if (aborter) {
                    aborter.abort();
                }
                await polling;
            };
        },
        { immediate: true },
    )

    return {
        ...toRefs(_caseState),
        shouldPoll,
        study: computed<ApiStudy | null>(
            () => _caseState.studies.find(s => s.self === _caseState.case?.study) ?? null
        ),
        templateType: (template: Template): 'auto' | 'user' | null => {
            switch (template.self) {
                case _caseState.case?.automatedTemplate:
                    return 'auto';
                case _caseState.case?.manualTemplate:
                    return 'user';
                default:
                    return null;
            }
        },
        duplicateStudy: async (
            study: ApiStudy,
            operation: DuplicateStudyOperation,
            options: StudyProcessOptionsRepresentation,
        ) => {
            const caseId = _caseState.caseId;
            if (caseId === null) {
                throw new Error('Cannot duplicate study: no case loaded');
            }

            const create_data: _StudyCopyCreateDataRepresentation = {
                name: study.name,
                study: study.self,
                operation,
                options,
                web_component_version: useVersion().webComponentVersion,
            };

            const { status, data, headers }: AxiosResponse = await client.post(
                `/project/${_caseState.caseId}/study`,
                create_data,
            );

            if (status === HttpStatusCode.Created) {
                return data?.id ?? getIdFromUrl(headers.location);
            }

            throw new AxiosError(`Failed to duplicate study from ${operation}: ${data?.detail}`);
        },
        duplicateTemplate: async (template: Template) => {
            const caseId = _caseState.caseId;
            if (caseId === null) {
                throw new Error('Cannot duplicate template: no case loaded');
            }
            return await createTemplate(caseId, {
                surgical_template: template.self,
                type: 'automated',
            });
        },
        generateZippedDicomFile: async (study: ApiStudy) => {
            const caseId = study.id.case;
            const studyId = study.id.study;
            if (caseId === null || studyId === null) {
                throw new Error('Cannot generate zipped DICOM file: no case or study loaded');
            }

            const { status, data, headers }: AxiosResponse = await client.post(
                `/project/${caseId}/study/${studyId}/file/zipped`
            );

            if (status === HttpStatusCode.Accepted) {
                return data?.id ?? getIdFromUrl(headers.location);
            }

            throw new AxiosError(`Failed to generate zipped DICOM file: ${data?.detail}`);
        }
    };
});

type _StudyCopyCreateDataRepresentation = {
    /**
     * Create a new study based on an exiting study.
     * The study must exist, and must be at a state that is consistent with copying data. i.e.
     *
     * Note: to make a new study using DICOM files, the study must be at 'CatStack' state
     */
    name: string;

    /** the URI of an existing study (that must be in the same project). */
    study: string;

    operation: DuplicateStudyOperation;

    /** An optional set of options to allow the caller to modify processing behaviour. */
    options?: StudyProcessOptionsRepresentation;

    web_component_version: string;
};

const _POLL_INTERVAL_MILLISECONDS = 1000 * 2; // 2 seconds

export async function _pollCase(
    caseId: number,
    onFetched: (result: _AdminCaseState) => void,
    signal: AbortSignal,
): Promise<void> {
    const config = { signal };
    try {
        while (!signal.aborted) {
            const case_ = await getCase(caseId);
            signal.throwIfAborted();

            const getStudies = async (): Promise<ApiStudy[]> => {
                const studyCollection = await client.get(case_.studies, config);
                return await Promise.all(
                    studyCollection.data.items.map((item: { id: string }) => getStudy(item.id, config))
                );
            };

            const getTemplates = async (): Promise<Template[]> => {
                const templateCollection = await client.get(case_.templates, config)
                return await Promise.all(
                    templateCollection.data.items.map((item: { id: string }) => getTemplate(item.id, config))
                );
            };

            const [ studies, templates ] = await Promise.all([ getStudies(), getTemplates() ])

            signal.throwIfAborted();

            onFetched({ caseId, case: case_, studies, templates });

            await asyncTimeout(_POLL_INTERVAL_MILLISECONDS, config.signal);
        }
    } catch (e: unknown) {
        if (signal.aborted) {
            return;
        }

        throw e;
    }
}
